import {
  ArrowLeftOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  PlusOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { PERMISSIONS } from '@navicosa/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errorText, fetchBlob } from '../../api/client';
import {
  ITEM_TYPE_LABEL,
  phoneLocal,
  type ProductListItem,
  QUOTE_STATUS_COLOR,
  QUOTE_STATUS_LABEL,
  type QuotationDetail,
  type QuotationItem,
  vnd,
} from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { ConvertModal } from './ConvertModal';

type Line = {
  key: string;
  id?: string;
  item_type: QuotationItem['item_type'];
  request_item_id: string | null;
  product_id: string | null;
  name: string;
  brand: string | null;
  model: string | null;
  quantity: number;
  rental_period: 'day' | 'week' | 'month' | null;
  period_count: string;
  unit_price: number;
  cost_unit_price: number | null;
  supplier_id: string | null;
  notes: string | null;
};

const UNIT_OPTIONS = [
  { value: 'day', label: 'ngày' },
  { value: 'week', label: 'tuần' },
  { value: 'month', label: 'tháng' },
];

const toLine = (i: QuotationItem): Line => ({
  key: i.id,
  id: i.id,
  item_type: i.item_type,
  request_item_id: i.request_item_id,
  product_id: i.product_id,
  name: i.product_name_snapshot,
  brand: i.brand_snapshot,
  model: i.model_snapshot,
  quantity: i.quantity,
  rental_period: i.rental_period,
  period_count: String(Number(i.period_count)),
  unit_price: i.unit_price,
  cost_unit_price: i.cost_unit_price ?? null,
  supplier_id: i.supplier_id ?? null,
  notes: i.notes,
});

/** Tạm tính phía trình duyệt (cùng công thức máy chủ); số chính thức lấy sau khi lưu. */
function preview(lines: Line[], discount: number, rate: number | null) {
  const subs = lines.map((l) =>
    Math.round(l.unit_price * l.quantity * (Number(l.period_count) || 0)),
  );
  const subtotal = subs.reduce((a, b) => a + b, 0);
  const taxable = Math.max(0, subtotal - discount);
  const tax = rate === null ? 0 : Math.round((taxable * rate) / 100);
  return { subs, subtotal, taxable, tax, total: taxable + tax };
}

let seq = 0;

export function QuotationPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { message, modal } = App.useApp();
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.QUOTATIONS_MANAGE);
  const canCost = can(PERMISSIONS.COST_READ);
  const canCostEdit = can(PERMISSIONS.COST_MANAGE);
  const canBook = can(PERMISSIONS.BOOKINGS_MANAGE);

  const q = useQuery({
    queryKey: ['quotation', id],
    queryFn: () => api.get<QuotationDetail>(`/admin/quotations/${id}`),
  });
  const products = useQuery({
    queryKey: ['products-pick'],
    queryFn: async () => (await api.page<ProductListItem>('/admin/products', { limit: 100 })).items,
    enabled: canManage,
  });

  const [lines, setLines] = useState<Line[]>([]);
  const [head, setHead] = useState({
    tax_rate: 8 as number | null,
    discount_amount: 0,
    valid_until: '',
    terms: '',
    customer_note: '',
    internal_note: '',
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState<null | 'accept' | 'reject' | 'cancel'>(null);
  const [reasonText, setReasonText] = useState('');
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (!q.data) return;
    setLines(q.data.items.map(toLine));
    setHead({
      tax_rate: q.data.tax_rate === null ? null : Number(q.data.tax_rate),
      discount_amount: q.data.discount_amount,
      valid_until: q.data.valid_until ?? '',
      terms: q.data.terms ?? '',
      customer_note: q.data.customer_note ?? '',
      internal_note: q.data.internal_note ?? '',
    });
    setDirty(false);
  }, [q.data]);

  const pv = useMemo(
    () => preview(lines, head.discount_amount, head.tax_rate),
    [lines, head.discount_amount, head.tax_rate],
  );

  if (q.isLoading) return <Spin />;
  if (!q.data)
    return <Alert type="error" message="Không tìm thấy báo giá hoặc bạn không có quyền xem" />;
  const d = q.data;
  const draft = d.status === 'draft' && canManage;
  const open = d.status === 'sent' || d.status === 'viewed';

  const setLine = (key: string, patch: Partial<Line>) => {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
    setDirty(true);
  };
  const addLine = (item_type: Line['item_type'], product?: ProductListItem) => {
    setLines((ls) => [
      ...ls,
      {
        key: `new-${++seq}`,
        item_type,
        request_item_id: null,
        product_id: product?.id ?? null,
        name:
          product?.name ??
          (item_type === 'transport'
            ? 'Vận chuyển'
            : item_type === 'handling'
              ? 'Nâng hạ / lắp đặt'
              : 'Chi phí khác'),
        brand: null,
        model: product?.model ?? null,
        quantity: 1,
        rental_period: item_type === 'rental' ? 'day' : null,
        period_count: '1',
        unit_price: 0,
        cost_unit_price: null,
        supplier_id: null,
        notes: null,
      },
    ]);
    setDirty(true);
  };

  const refresh = (data: QuotationDetail) => {
    qc.setQueryData(['quotation', id], data);
    void qc.invalidateQueries({ queryKey: ['quotations'] });
    void qc.invalidateQueries({ queryKey: ['request', d.request_id] });
  };
  const run = async (fn: () => Promise<QuotationDetail>, ok: string) => {
    try {
      const r = await fn();
      if (r.id !== d.id) nav(`/quotations/${r.id}`);
      else refresh(r);
      message.success(ok);
      return true;
    } catch (e) {
      message.error(errorText(e));
      return false;
    }
  };

  const save = async () => {
    setSaving(true);
    await run(
      () =>
        api.patch<QuotationDetail>(`/admin/quotations/${id}`, {
          tax_rate: head.tax_rate,
          discount_amount: head.discount_amount,
          valid_until: head.valid_until || undefined,
          terms: head.terms || null,
          customer_note: head.customer_note || null,
          internal_note: head.internal_note || null,
          items: lines.map((l) => ({
            id: l.id,
            item_type: l.item_type,
            request_item_id: l.request_item_id,
            product_id: l.product_id,
            name: l.name,
            brand: l.brand,
            model: l.model,
            quantity: l.quantity,
            rental_period: l.rental_period,
            period_count: l.period_count,
            unit_price: l.unit_price,
            ...(canCostEdit
              ? { cost_unit_price: l.cost_unit_price, supplier_id: l.supplier_id }
              : {}),
            notes: l.notes,
          })),
        }),
      'Đã lưu bản nháp',
    );
    setSaving(false);
  };

  const openPdf = async () => {
    let blob: Blob;
    try {
      blob = await fetchBlob(`/admin/quotations/${id}/pdf`);
    } catch (e) {
      return message.error(errorText(e));
    }
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const columns = [
    {
      title: 'Hạng mục',
      width: 230,
      render: (_: unknown, l: Line) => (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Tag>{ITEM_TYPE_LABEL[l.item_type]}</Tag>
          {draft ? (
            <Input value={l.name} onChange={(e) => setLine(l.key, { name: e.target.value })} />
          ) : (
            <span>
              {[l.name, [l.brand, l.model].filter(Boolean).join(' ')].filter(Boolean).join(' – ')}
            </span>
          )}
          {draft ? (
            <Input
              size="small"
              placeholder="Ghi chú dòng (in trên báo giá)"
              value={l.notes ?? ''}
              onChange={(e) => setLine(l.key, { notes: e.target.value || null })}
            />
          ) : (
            l.notes && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {l.notes}
              </Typography.Text>
            )
          )}
        </Space>
      ),
    },
    {
      title: 'SL',
      width: 76,
      render: (_: unknown, l: Line) =>
        draft ? (
          <InputNumber
            min={1}
            max={100}
            value={l.quantity}
            onChange={(v) => setLine(l.key, { quantity: v ?? 1 })}
            style={{ width: 64 }}
          />
        ) : (
          l.quantity
        ),
    },
    {
      title: 'Đơn vị · Số kỳ',
      width: 150,
      render: (_: unknown, l: Line) =>
        draft ? (
          <Space.Compact>
            {l.item_type === 'rental' ? (
              <Select
                style={{ width: 80 }}
                value={l.rental_period}
                options={UNIT_OPTIONS}
                onChange={(v) => setLine(l.key, { rental_period: v })}
              />
            ) : (
              <Input style={{ width: 72 }} disabled value="lần" />
            )}
            <InputNumber
              min={0.01}
              step={1}
              precision={2}
              style={{ width: 80 }}
              value={Number(l.period_count)}
              onChange={(v) => setLine(l.key, { period_count: String(v ?? 1) })}
            />
          </Space.Compact>
        ) : (
          `${Number(l.period_count)} ${l.rental_period ? UNIT_OPTIONS.find((o) => o.value === l.rental_period)!.label : 'lần'}`
        ),
    },
    {
      title: 'Đơn giá (chưa VAT)',
      width: 134,
      align: 'right' as const,
      render: (_: unknown, l: Line) =>
        draft ? (
          <InputNumber<number>
            min={0}
            step={50_000}
            value={l.unit_price}
            status={l.item_type === 'rental' && l.unit_price <= 0 ? 'warning' : undefined}
            formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
            parser={(v) => Number((v ?? '').replace(/\./g, ''))}
            onChange={(v) => setLine(l.key, { unit_price: v ?? 0 })}
            style={{ width: 120 }}
          />
        ) : (
          vnd(l.unit_price)
        ),
    },
    ...(canCost
      ? [
          {
            title: 'Giá nhập / đơn vị',
            width: 140,
            align: 'right' as const,
            render: (_: unknown, l: Line) =>
              draft && canCostEdit ? (
                <InputNumber<number>
                  min={0}
                  step={50_000}
                  value={l.cost_unit_price ?? undefined}
                  placeholder="—"
                  formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(v) => Number((v ?? '').replace(/\./g, ''))}
                  onChange={(v) => setLine(l.key, { cost_unit_price: v ?? null })}
                  style={{ width: 124 }}
                />
              ) : l.cost_unit_price === null ? (
                '—'
              ) : (
                <Tooltip title={d.items.find((i) => i.id === l.id)?.supplier?.name}>
                  {vnd(l.cost_unit_price)}
                </Tooltip>
              ),
          },
        ]
      : []),
    {
      title: 'Thành tiền',
      width: 110,
      fixed: 'right' as const,
      align: 'right' as const,
      render: (_: unknown, __: Line, i: number) => vnd(pv.subs[i] ?? 0),
    },
    ...(draft
      ? [
          {
            title: '',
            width: 40,
            render: (_: unknown, l: Line) => (
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => (
                  setLines((ls) => ls.filter((x) => x.key !== l.key)),
                  setDirty(true)
                )}
              />
            ),
          },
        ]
      : []),
  ];

  const r = d.request;
  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav(-1)}>
          Quay lại
        </Button>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Báo giá {d.quote_code}
        </Typography.Title>
        <Tag color={QUOTE_STATUS_COLOR[d.status]}>{QUOTE_STATUS_LABEL[d.status]}</Tag>
        {d.booking && (
          <Link to={`/bookings/${d.booking.id}`}>
            <Tag color="green">Đơn {d.booking.booking_code}</Tag>
          </Link>
        )}
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={17}>
          <Card
            title="Các dòng báo giá"
            extra={
              draft && (
                <Space wrap>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="+ Thêm thiết bị"
                    style={{ width: 220 }}
                    value={null}
                    options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
                    onChange={(v) =>
                      addLine(
                        'rental',
                        products.data?.find((p) => p.id === v),
                      )
                    }
                  />
                  <Button icon={<PlusOutlined />} onClick={() => addLine('transport')}>
                    Vận chuyển
                  </Button>
                  <Button icon={<PlusOutlined />} onClick={() => addLine('handling')}>
                    Nâng hạ
                  </Button>
                  <Button icon={<PlusOutlined />} onClick={() => addLine('other')}>
                    Khác
                  </Button>
                </Space>
              )
            }
          >
            <Table<Line>
              rowKey="key"
              size="small"
              pagination={false}
              dataSource={lines}
              columns={columns}
              scroll={{ x: 900 }}
            />
            <Row justify="end" style={{ marginTop: 16 }}>
              <Col xs={24} md={16}>
                <Descriptions
                  column={1}
                  size="small"
                  bordered
                  styles={{ label: { width: 150, whiteSpace: 'nowrap' } }}
                >
                  <Descriptions.Item label="Cộng (chưa VAT)">{vnd(pv.subtotal)}</Descriptions.Item>
                  <Descriptions.Item label="Chiết khấu">
                    {draft ? (
                      <Space>
                        <InputNumber<number>
                          min={0}
                          step={100_000}
                          value={head.discount_amount}
                          formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                          parser={(v) => Number((v ?? '').replace(/\./g, ''))}
                          onChange={(v) => (
                            setHead((h) => ({ ...h, discount_amount: v ?? 0 })),
                            setDirty(true)
                          )}
                          style={{ width: 140 }}
                        />
                        <Typography.Text type="secondary">
                          {pv.subtotal
                            ? ((head.discount_amount * 100) / pv.subtotal).toFixed(2)
                            : 0}
                          %
                        </Typography.Text>
                      </Space>
                    ) : (
                      `${vnd(d.discount_amount)} (${d.discount_percent}%)`
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Thuế">
                    {draft ? (
                      <Select
                        style={{ width: 260 }}
                        value={head.tax_rate}
                        options={d.tax_options.map((o) => ({ value: o.rate, label: o.label }))}
                        onChange={(v) => (setHead((h) => ({ ...h, tax_rate: v })), setDirty(true))}
                      />
                    ) : (
                      (d.tax_options.find((o) => o.rate === Number(d.tax_rate))?.label ??
                      `${d.tax_rate}%`)
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Tiền thuế">{vnd(pv.tax)}</Descriptions.Item>
                  <Descriptions.Item label={<b>Tổng cộng</b>}>
                    <Typography.Text strong style={{ fontSize: 16, color: '#DD5816' }}>
                      {vnd(pv.total)}
                    </Typography.Text>
                    {dirty && (
                      <Tag color="orange" style={{ marginLeft: 8 }}>
                        Tạm tính – chưa lưu
                      </Tag>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
            {canCost && d.cost_summary && d.cost_summary.lines_with_cost > 0 && (
              <Alert
                style={{ marginTop: 12 }}
                type="info"
                showIcon
                message={`Lãi gộp các dòng có giá nhập: ${d.cost_summary.margin_percent ?? '—'}% (doanh thu ${vnd(d.cost_summary.revenue_with_cost)}, giá vốn ${vnd(d.cost_summary.cost_total)}) – chỉ quản lý thấy`}
              />
            )}
          </Card>

          <Card title="Ghi chú & điều khoản" style={{ marginTop: 16 }}>
            <Typography.Text type="secondary">Ghi chú gửi khách (in trên báo giá)</Typography.Text>
            <Input.TextArea
              rows={2}
              disabled={!draft}
              value={head.customer_note}
              onChange={(e) => (
                setHead((h) => ({ ...h, customer_note: e.target.value })),
                setDirty(true)
              )}
            />
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
              Điều khoản (in trên báo giá)
            </Typography.Text>
            <Input.TextArea
              rows={5}
              disabled={!draft}
              value={head.terms}
              onChange={(e) => (setHead((h) => ({ ...h, terms: e.target.value })), setDirty(true))}
            />
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
              Ghi chú nội bộ (khách không thấy)
            </Typography.Text>
            <Input.TextArea
              rows={2}
              disabled={!draft}
              value={head.internal_note}
              onChange={(e) => (
                setHead((h) => ({ ...h, internal_note: e.target.value })),
                setDirty(true)
              )}
            />
          </Card>
        </Col>

        <Col xs={24} xl={7}>
          <Card title="Thao tác">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space wrap>
                <Typography.Text type="secondary">Hiệu lực đến</Typography.Text>
                {draft ? (
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={head.valid_until ? dayjs(head.valid_until) : null}
                    disabledDate={(x) => x.isBefore(dayjs(), 'day')}
                    onChange={(v) => (
                      setHead((h) => ({ ...h, valid_until: v ? v.toISOString() : '' })),
                      setDirty(true)
                    )}
                  />
                ) : (
                  <b>{d.valid_until ? dayjs(d.valid_until).format('DD/MM/YYYY') : '—'}</b>
                )}
              </Space>
              {draft && (
                <>
                  <Button
                    type="primary"
                    block
                    loading={saving}
                    disabled={!dirty}
                    onClick={() => void save()}
                  >
                    Lưu bản nháp
                  </Button>
                  {!dirty && d.send_problems.length > 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      message="Chưa gửi được"
                      description={
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {d.send_problems.map((p) => (
                            <li key={p}>{p}</li>
                          ))}
                        </ul>
                      }
                    />
                  )}
                  <Button
                    block
                    icon={<SendOutlined />}
                    disabled={dirty || !d.can_send}
                    onClick={() =>
                      modal.confirm({
                        title: `Gửi báo giá ${d.quote_code}?`,
                        content:
                          'Sau khi gửi, nội dung báo giá được khóa. Muốn điều chỉnh phải tạo phiên bản mới.',
                        okText: 'Gửi',
                        onOk: () =>
                          run(
                            () => api.post(`/admin/quotations/${id}/send`),
                            'Đã gửi – tải PDF để gửi khách qua Zalo',
                          ),
                      })
                    }
                  >
                    Đánh dấu đã gửi khách
                  </Button>
                </>
              )}
              <Button
                block
                icon={<FilePdfOutlined />}
                disabled={dirty}
                onClick={() => void openPdf()}
              >
                Xem / tải PDF
              </Button>
              {open && canManage && (
                <>
                  <Button
                    block
                    type="primary"
                    onClick={() => (setReason('accept'), setReasonText(''))}
                  >
                    Khách đồng ý (xác nhận thay)
                  </Button>
                  <Button block danger onClick={() => (setReason('reject'), setReasonText(''))}>
                    Khách từ chối
                  </Button>
                </>
              )}
              {canManage &&
                ['sent', 'viewed', 'rejected', 'expired', 'cancelled'].includes(d.status) &&
                !['converted', 'cancelled', 'rejected'].includes(r.status) && (
                  <Button
                    block
                    onClick={() =>
                      void run(
                        () => api.post(`/admin/quotations/${id}/revise`),
                        'Đã tạo phiên bản mới',
                      )
                    }
                  >
                    Tạo phiên bản mới
                  </Button>
                )}
              {d.status === 'accepted' &&
                !d.booking &&
                (canBook ? (
                  <Button block type="primary" onClick={() => setConverting(true)}>
                    Chuyển thành đơn thuê
                  </Button>
                ) : (
                  <Alert
                    type="info"
                    showIcon
                    message="Khách đã chấp nhận – chờ điều phối chuyển đơn thuê"
                  />
                ))}
              {canManage && (d.status === 'draft' || open) && (
                <Button
                  block
                  type="text"
                  danger
                  onClick={() => (setReason('cancel'), setReasonText(''))}
                >
                  Hủy báo giá
                </Button>
              )}
            </Space>
          </Card>

          <Card
            title="Yêu cầu"
            style={{ marginTop: 16 }}
            extra={<Link to={`/requests/${r.id}`}>{r.request_code}</Link>}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Khách">
                {[r.contact_company, r.contact_name].filter(Boolean).join(' – ')}
              </Descriptions.Item>
              <Descriptions.Item label="SĐT">{phoneLocal(r.contact_phone)}</Descriptions.Item>
              <Descriptions.Item label="Công trình">
                {r.delivery_address}
                {r.location ? ` (${r.location.name})` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Thuê">
                {dayjs(r.rental_start_at).format('DD/MM/YYYY')}
                {r.rental_end_at ? ` đến ${dayjs(r.rental_end_at).format('DD/MM/YYYY')}` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Phụ trách">
                {r.assigned_user?.full_name ?? '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Lịch sử" style={{ marginTop: 16 }}>
            <Timeline
              items={d.status_history.map((h) => ({
                children: (
                  <>
                    <b>{QUOTE_STATUS_LABEL[h.to_status]}</b>
                    <div style={{ fontSize: 12, color: '#737B86' }}>
                      {dayjs(h.created_at).format('DD/MM/YYYY HH:mm')} ·{' '}
                      {h.user?.full_name ?? 'Khách'}
                    </div>
                    {h.note && <div>{h.note}</div>}
                  </>
                ),
              }))}
            />
            {d.reject_reason && (
              <Alert type="error" message={`Lý do từ chối: ${d.reject_reason}`} />
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        open={reason !== null}
        title={
          reason === 'accept'
            ? 'Khách đồng ý báo giá'
            : reason === 'reject'
              ? 'Khách từ chối báo giá'
              : 'Hủy báo giá'
        }
        okText="Xác nhận"
        okButtonProps={{
          danger: reason !== 'accept',
          disabled: reason === 'reject' && reasonText.trim().length < 3,
        }}
        onCancel={() => setReason(null)}
        onOk={async () => {
          const ok = await run(
            () =>
              reason === 'accept'
                ? api.post(`/admin/quotations/${id}/accept`, { note: reasonText.trim() || null })
                : reason === 'reject'
                  ? api.post(`/admin/quotations/${id}/reject`, { reason: reasonText.trim() })
                  : api.post(`/admin/quotations/${id}/cancel`, { note: reasonText.trim() || null }),
            'Đã cập nhật',
          );
          if (ok) setReason(null);
        }}
      >
        {reason === 'accept' && (
          <Typography.Paragraph type="secondary">
            Ghi nhận khách đã đồng ý qua Zalo/điện thoại. Hệ thống lưu là "nhân viên xác nhận thay".
          </Typography.Paragraph>
        )}
        <Input.TextArea
          rows={3}
          value={reasonText}
          onChange={(e) => setReasonText(e.target.value)}
          placeholder={
            reason === 'reject'
              ? 'Lý do khách từ chối (bắt buộc)'
              : 'Ghi chú (không bắt buộc), ví dụ: khách chốt qua Zalo lúc 9h'
          }
        />
      </Modal>

      {converting && (
        <ConvertModal
          quotation={d}
          onClose={() => setConverting(false)}
          onDone={(bid) => nav(`/bookings/${bid}`)}
        />
      )}
    </>
  );
}
